"use client";

/**
 * La mesure du parcours harmonique.
 *
 * # Ce qu'elle répare
 *
 * Le produit a deux parcours d'apprentissage, et un seul était mesuré. Du sas
 * jusqu'à la dernière unité enchaînée, l'échelle harmonique n'émettait rien :
 * ni session, ni étape, ni sortie. Comparer l'efficacité des deux parcours —
 * la question pédagogique centrale — se tranchait donc à l'avis, faute du
 * moindre chiffre du côté harmonique.
 *
 * Les noms d'événements sont ceux du parcours par cœur, paramétrés par
 * `journey`. C'est le point du champ : deux séries d'événements parallèles
 * auraient rendu la comparaison impossible dans l'outil même qui doit la
 * rendre lisible. Corollaire : les propriétés qui portent la comparaison
 * doivent vouloir dire la même chose des deux côtés — d'où `pieceId`, qui est
 * l'identité **catalogue** ici comme là-bas, et `completed`, dont la
 * définition est écrite dans les deux émetteurs.
 *
 * # Pourquoi un module et pas seulement un hook
 *
 * L'écran est à deux étages. `HarmonyLevel` tient le sas, la fin de niveau et
 * le cas où le niveau ne pose rien sous les mains ; `HarmonyScene` tient la
 * couture et l'unité, et elle seule sait laquelle des deux passe devant. Faire
 * remonter cette information par un état ferait rendre le parent une seconde
 * fois pour une mesure, ce qui est exactement le prix qu'une mesure n'a pas le
 * droit de coûter. Le module est donc le point de rendez-vous, comme
 * {@link import("@/lib/analytics/studio-session")} l'est pour le studio.
 *
 * # L'écran nommé avant que la session n'existe
 *
 * React joue les effets des enfants avant ceux du parent : la scène nomme son
 * écran alors que le niveau n'a pas encore ouvert la session. Le premier
 * `journey_phase_entered` du parcours — celui qui porte tout le dénominateur —
 * tomberait donc systématiquement dans le vide. Il est mis de côté et rejoué à
 * l'ouverture, après `practice_session_started`, pour que l'ordre de la file
 * raconte l'ordre des faits. La même case sert au retour au premier plan, où
 * l'écran n'est pas réannoncé non plus : voir `endHarmonySession`.
 */

import { useEffect } from "react";

import { track } from "@/lib/analytics";
import { resumableOnDeparture } from "@/lib/analytics/departure";

/**
 * Les écrans du parcours harmonique, nommés d'après le code qui les rend et
 * non d'après une invention : `SasScreen`, `SeamScreen`, `HarmonyScene`, et
 * les deux impasses de `HarmonyLevel`.
 */
export type HarmonyPhase = "nothing_to_play" | "sas" | "seam" | "unit" | "level_done";

/**
 * L'ordre des écrans, pour retenir le plus avancé.
 *
 * La tête de lecture fait revenir en arrière — un clic dans la minimap rouvre
 * l'unité qu'on désigne — donc le dernier écran vu n'est pas celui où la
 * session est allée. Un abandon se lit par l'endroit qu'il a atteint.
 */
const PHASE_RANK: Record<HarmonyPhase, number> = {
  nothing_to_play: 0,
  sas: 1,
  seam: 2,
  unit: 3,
  level_done: 4,
};

interface HarmonySession {
  /** Le niveau travaillé. Changer de barreau est un autre parcours. */
  rungId: string;
  /**
   * L'identité **catalogue** du morceau sous les mains, la même clé que
   * `piece_opened` et `studio_session_ended`.
   *
   * Sans elle, les sessions harmoniques ne se joignaient à rien : le champ
   * `journey` promettait de comparer les deux parcours et la comparaison
   * s'arrêtait au premier « sur quel morceau ». `undefined` quand le morceau
   * ne vient pas du catalogue.
   */
  pieceId?: string;
  startedAt: number;
  /** Combien d'unités ce niveau donne à travailler, le dénominateur. */
  unitCount: number;
  /** L'écran le plus avancé atteint pendant la session. */
  reached: HarmonyPhase | null;
  /**
   * Les couples écran/unité déjà émis.
   *
   * L'unité montrée suit le transport : une lecture qui traverse le niveau, et
   * plus encore une boucle, repasserait sur les mêmes unités. Sans cet
   * ensemble, `journey_phase_entered` deviendrait un compteur de passages de
   * tête de lecture, ce que la règle de la maison interdit.
   */
  seen: Set<string>;
  /** Les unités dont l'écran de jeu a été atteint. */
  opened: Set<string>;
  /** Les unités enchaînées pendant cette session. */
  chained: Set<string>;
  /** Le dernier écran annoncé, pour le rejouer dans une session rouverte. */
  last: { phase: HarmonyPhase; unitId?: string } | null;
}

let current: HarmonySession | null = null;
/** L'écran nommé par la scène avant que le niveau n'ait ouvert la session. */
let pending: { rungId: string; phase: HarmonyPhase; unitId?: string } | null = null;

/** Ce qu'il faut pour rouvrir le même niveau au retour au premier plan. */
interface HarmonySubject {
  readonly rungId: string;
  readonly unitCount: number;
  readonly pieceId?: string;
}

/**
 * Le départ, le retour, et la mémoire du sujet entre les deux.
 *
 * Le sujet est oublié dès qu'une sortie d'écran ferme la session, sans quoi un
 * retour au premier plan rouvrirait une session de niveau pour un écran
 * démonté.
 */
const departure = resumableOnDeparture<HarmonySubject>({
  close: () => {
    endHarmonySession("pagehide");
  },
  reopen: (subject) => {
    startHarmonySession(subject.rungId, subject.unitCount, subject.pieceId);
  },
});

/**
 * Ouvre la session du niveau, en fermant la précédente s'il y en avait une.
 *
 * Idempotent sur un même barreau : le nombre d'unités et l'identité du morceau
 * se précisent quand le morceau arrive, et un rappel de plus ne doit pas
 * couper la session en deux.
 */
function startHarmonySession(
  rungId: string,
  unitCount: number,
  pieceId?: string,
): void {
  if (current !== null && current.rungId === rungId) {
    current.unitCount = unitCount;
    current.pieceId = pieceId;
    // Rafraîchi et pas seulement posé : le nombre d'unités et l'identité du
    // morceau se précisent après l'ouverture, et une session rouverte sur les
    // valeurs du premier rendu repartirait sans morceau.
    departure.opened({ rungId, unitCount, pieceId });

    return;
  }

  // Avant d'écrire le sujet : la fermeture pour sortie d'écran l'efface, et
  // l'ordre inverse jetterait celui qu'on vient de poser.
  endHarmonySession("navigation");

  departure.opened({ rungId, unitCount, pieceId });

  current = {
    rungId,
    pieceId,
    startedAt: Date.now(),
    unitCount,
    reached: null,
    seen: new Set(),
    opened: new Set(),
    chained: new Set(),
    last: null,
  };

  track({
    name: "practice_session_started",
    properties: { journey: "harmonic", pieceId },
  });

  if (pending !== null && pending.rungId === rungId) {
    const held = pending;
    pending = null;
    recordHarmonyPhase(held.rungId, held.phase, held.unitId);
  }
}

/** Ferme la session en cours, s'il y en a une. */
function endHarmonySession(reason: "navigation" | "pagehide"): void {
  if (current === null) {
    return;
  }

  const session = current;
  current = null;

  // Seulement ce qui appartenait à cette session : sur un changement de
  // barreau, la scène a déjà nommé l'écran du **suivant**, et le jeter ici
  // ferait perdre le premier écran de chaque niveau après le premier.
  if (pending !== null && pending.rungId === session.rungId) {
    pending = null;
  }

  // Le sujet, lui, est l'affaire de l'enveloppe : c'est elle qui déclenche la
  // fermeture d'un départ, donc elle seule sait laquelle en est un.
  departure.closed();

  if (reason === "pagehide" && session.last !== null) {
    // Le départ n'est peut-être qu'un passage en arrière-plan, et l'enveloppe
    // rouvrira. L'écran, lui, ne sera pas réannoncé : il est posé par un effet
    // qui ne rejouera pas au retour. On le met de côté pour que la session
    // rouverte sache où elle se tient, faute de quoi elle rapporterait un
    // `phaseReached` vide — c'est-à-dire sur toutes les séances de tablette.
    pending = {
      rungId: session.rungId,
      phase: session.last.phase,
      unitId: session.last.unitId,
    };
  }

  track({
    name: "practice_session_ended",
    properties: {
      journey: "harmonic",
      pieceId: session.pieceId,
      durationMs: Date.now() - session.startedAt,
      phrasesAttempted: session.opened.size,
      phrasesValidated: session.chained.size,
      phrasesTotal: session.unitCount,
      phaseReached: session.reached ?? undefined,
      // `completed` dit l'**état du parcours** à la fermeture : plus une seule
      // unité ne reste à valider. C'est mot pour mot la définition du parcours
      // par cœur (`use-journey-analytics`), et elle est écrite des deux côtés
      // parce qu'un même champ qui voudrait dire « terminé » ici et « terminé
      // aujourd'hui » là-bas ne serait comparable nulle part — or comparer les
      // deux parcours est tout l'objet de `journey`.
      //
      // Le niveau harmonique ne garde aucune progression d'une séance à
      // l'autre : il n'existe donc pas d'unité validée ailleurs qu'ici, et
      // « toutes les unités validées » se lit sur ce que cette session a
      // enchaîné. Ce que la séance a produit reste lisible à côté, dans
      // `phrasesValidated`, comme pour l'autre parcours.
      completed: session.unitCount > 0 && session.chained.size >= session.unitCount,
      reason,
    },
  });
}

/**
 * L'écran affiché vient de changer.
 *
 * Appelé par celui qui **rend** l'écran, et par lui seul : c'est la seule
 * façon que la couture et l'unité ne se contredisent pas, puisque le niveau ne
 * sait pas laquelle des deux la scène met devant.
 *
 * `rungId` accompagne l'écran parce que la scène parle **avant** que le niveau
 * n'ait basculé de barreau : sans lui, le premier écran d'un niveau se
 * rangerait dans la session du précédent, qui n'est pas encore fermée.
 */
export function recordHarmonyPhase(
  rungId: string,
  phase: HarmonyPhase,
  unitId?: string,
): void {
  if (current === null || current.rungId !== rungId) {
    // Voir l'en-tête : la scène parle avant que le niveau n'ait ouvert la
    // session. Un seul écran mis de côté, le premier, qui est aussi le seul à
    // pouvoir précéder l'ouverture.
    pending = { rungId, phase, unitId };

    return;
  }

  current.last = { phase, unitId };

  if (current.reached === null || PHASE_RANK[phase] > PHASE_RANK[current.reached]) {
    current.reached = phase;
  }

  if (phase === "unit" && unitId !== undefined) {
    current.opened.add(unitId);
  }

  const key = `${phase}:${unitId ?? ""}`;

  if (current.seen.has(key)) {
    return;
  }

  current.seen.add(key);

  track({
    name: "journey_phase_entered",
    properties: {
      journey: "harmonic",
      phase,
      phraseId: unitId,
      pieceId: current.pieceId,
    },
  });
}

/**
 * Une unité vient d'être enchaînée.
 *
 * Compté et non émis à l'unité : `phrase_validated` n'a pas de champ qui dise
 * de quel parcours il parle, et l'émettre ici mélangerait deux pédagogies sur
 * la métrique la plus importante du produit. Le compte part dans l'enveloppe
 * de session, où `journey` le sépare.
 */
export function recordHarmonyUnitChained(rungId: string, unitId: string): void {
  if (current !== null && current.rungId === rungId) {
    current.chained.add(unitId);
  }
}

/** Remet le module à zéro. Réservé aux tests. */
export function resetHarmonySessionForTests(): void {
  current = null;
  pending = null;
  departure.reset();
}

/**
 * La session de pratique du niveau ouvert, encadrée par l'écran qui le tient.
 *
 * `unitCount` arrive après le barreau — le découpage attend le morceau — et
 * `pieceId` de même, d'où deux effets : la sortie ne peut pas être armée sur
 * des valeurs qui se précisent, sinon la session se fermerait à la première
 * mesure du plan.
 */
export function useHarmonyAnalytics(opts: {
  readonly rungId: string;
  readonly unitCount: number;
  /**
   * L'identité catalogue du morceau sous les mains, `null` quand il n'en vient
   * pas. C'est elle, et jamais la clé de progression, qui part dans `pieceId`.
   */
  readonly pieceId: string | null;
}): void {
  const { rungId, unitCount, pieceId } = opts;

  useEffect(() => {
    startHarmonySession(rungId, unitCount, pieceId ?? undefined);
  }, [rungId, unitCount, pieceId]);

  useEffect(() => {
    return () => {
      endHarmonySession("navigation");
    };
  }, []);
}
